/** Same-ID authoring revision owner for the existing form; no second form or dirty state. */
import { useCallback, useRef, useState } from 'react'
import { canonicalJson } from '@testfeed/core'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { MerchantFacingError } from '../../merchantErrors'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  studyAuthoringDraftSchema,
  retainStudyAuthoringSource,
  type StudyAuthoringRecord,
  type StudyAuthoringState,
  type StudyBuilderSnapshot,
} from '@testfeed/shopify-contracts'
import type { ShopifyAppBridge } from '../../shopifyApiFetch'
import type { VerifiedTestFeedConnectionScope } from '../../ShopifyAppContext'
import type { StudyBuilderStart } from './studyBuilderStart'
import { buildStudyBuilderDraftPayload } from './studyBuilderDrafts'
import {
  createStudyAuthoring,
  saveStudyAuthoring,
  prepareStudyAuthoring,
  readStudyAuthoringStatus,
  readStudyAuthoring,
} from './studyAuthoringClient'

export function studyAuthoringQueryPrefix(scope: VerifiedTestFeedConnectionScope) {
  return ['shopify-study-authoring', scope.shopDomain, scope.orgId, scope.connectionId] as const
}

export function useStudyAuthoringEditor(
  shopify: ShopifyAppBridge,
  scope: VerifiedTestFeedConnectionScope,
  start: StudyBuilderStart,
  creationIntentId: string,
  onSaved: (saved: StudyAuthoringRecord) => void,
) {
  const client = useQueryClient()
  const current = useRef<StudyAuthoringRecord | null>(start.authoring?.authoring ?? null)
  const uncertain = useRef<{ base: StudyAuthoringRecord; draft: StudyAuthoringRecord['draft'] } | null>(null)
  const [hasUncertainSave, setHasUncertainSave] = useState(false)
  // One exact attempt owns uncertainty. The ref protects immediate navigation; state updates the Save Bar.
  const setUncertain = useCallback((attempt: typeof uncertain.current) => {
    uncertain.current = attempt
    setHasUncertainSave(attempt !== null)
  }, [])
  const readUncertainSave = useCallback(() => uncertain.current !== null, [])
  const creation = useRef<StudyAuthoringRecord['draft'] | null>(null)
  const [record, setRecord] = useState<StudyAuthoringRecord | null>(start.authoring?.authoring ?? null)
  const acknowledge = useCallback(
    (saved: StudyAuthoringRecord) => {
      current.current = saved
      setRecord(saved)
      onSaved(saved)
    },
    [onSaved],
  )
  const query = useQuery({
    queryKey: [...studyAuthoringQueryPrefix(scope), 'status', record?.studyId, record?.revision],
    enabled: Boolean(start.direct && record),
    retry: false,
    queryFn: ({ signal }) => readStudyAuthoringStatus(shopify, current.current!.studyId, signal),
    // The page has just read this test to open it, so the opening revision seeds the query and is not read twice.
    // Only that revision: after a save the record moves to a new revision (a new key), and seeding it with the old
    // bootstrap answer under `staleTime: Infinity` would wedge the editor on a status that never matches the record,
    // so a new revision reads once instead. Accepted evaluations keep this status current afterward.
    initialData: record && start.authoring?.authoring.revision === record.revision ? start.authoring : undefined,
    staleTime: Infinity,
    // Moving state is refreshed by evaluation's server-chosen retry delay; do not poll the same owner a second time.
  })
  const reconcile = useCallback(
    async (signal: AbortSignal) => {
      const attempt = uncertain.current
      if (!attempt) return current.current
      const stored = await readStudyAuthoring(shopify, attempt.base.studyId, signal)
      signal.throwIfAborted()
      // Web preserves source provenance at persistence. Use its shared rule only to recognize a lost acknowledgement;
      // never rewrite the evaluated document sent to Save.
      const expected = retainStudyAuthoringSource(attempt.draft, attempt.base.draft)
      const committed =
        expected.success &&
        stored.revision === attempt.base.revision + 1 &&
        canonicalJson(stored.draft) === canonicalJson(expected.draft)
      const unchanged =
        stored.revision === attempt.base.revision && canonicalJson(stored.draft) === canonicalJson(attempt.base.draft)
      if (!committed && !unchanged) {
        throw new MerchantFacingError(shopifyComponentCopy.shopifyApiErrors.reviewAgain033, {
          code: 'STUDY_REVISION_CONFLICT',
          status: 409,
        })
      }
      // A read failure/conflict leaves the attempt unresolved and the editor dirty.
      setUncertain(null)
      if (committed) acknowledge(stored)
      return stored
    },
    [acknowledge, setUncertain, shopify],
  )
  const persist = useCallback(
    async (snapshot: StudyBuilderSnapshot, signal: AbortSignal) => {
      const draft = studyAuthoringDraftSchema.parse(buildStudyBuilderDraftPayload(snapshot))
      let saved = await reconcile(signal)
      if (!saved) {
        // A lost create reply retries its original request before applying newer form edits.
        creation.current ??= draft
        saved = await createStudyAuthoring(shopify, { creationIntentId, draft: creation.current }, signal)
        signal.throwIfAborted()
        acknowledge(saved)
      }
      signal.throwIfAborted()
      if (canonicalJson(saved.draft) !== canonicalJson(draft)) {
        setUncertain({ base: saved, draft })
        try {
          saved = await saveStudyAuthoring(
            shopify,
            { studyId: saved.studyId, expectedRevision: saved.revision, draft },
            signal,
          )
          signal.throwIfAborted()
          setUncertain(null)
          acknowledge(saved)
        } catch (error) {
          if (error instanceof MerchantFacingError && error.code === 'STUDY_RUN_ACCEPTED')
            void client.invalidateQueries({ queryKey: studyAuthoringQueryPrefix(scope) })
          // Definitive server refusals did not write. Network/invalid replies and
          // aborts can follow a committed write, so the next action must read first.
          if (
            error instanceof MerchantFacingError &&
            error.status !== undefined &&
            error.status >= 400 &&
            error.status < 500 &&
            error.status !== 408 &&
            error.status !== 429
          )
            setUncertain(null)
          throw error
        }
      }
      return saved
    },
    [acknowledge, client, creationIntentId, reconcile, scope, setUncertain, shopify],
  )
  const prepare = useCallback(
    async (snapshot: StudyBuilderSnapshot, signal: AbortSignal) => {
      const saved = await persist(snapshot, signal)
      const response = await prepareStudyAuthoring(
        shopify,
        {
          studyId: saved.studyId,
          expectedRevision: saved.revision,
          draft: saved.draft,
        },
        signal,
      ).catch((error: unknown) => {
        void client.invalidateQueries({ queryKey: studyAuthoringQueryPrefix(scope) })
        throw error
      })
      acknowledge(response.authoring)
      client.setQueryData(
        [...studyAuthoringQueryPrefix(scope), 'status', response.authoring.studyId, response.authoring.revision],
        response,
      )
      return response
    },
    [acknowledge, client, persist, scope, shopify],
  )
  const state: StudyAuthoringState | null =
    query.data && record && query.data.authoring.revision === record.revision ? query.data.state : null
  const observeState = useCallback(
    (next: StudyAuthoringState | null) => {
      const saved = current.current
      if (!next || !saved || next.revision !== saved.revision) return
      const queryKey = [...studyAuthoringQueryPrefix(scope), 'status', saved.studyId, saved.revision]
      // The accepted evaluation just read this same owner. Stop an older status read from replacing its answer.
      void client.cancelQueries({ queryKey, exact: true }, { revert: false })
      client.setQueryData(queryKey, { authoring: saved, state: next })
    },
    [client, scope],
  )
  return { record, state, query, persist, prepare, reconcile, observeState, hasUncertainSave, readUncertainSave }
}
